// src/booksApi.ts
// Thin client for /api/books — see api/src/books.ts — and useBooks, which
// loads the list once per component that needs it.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './api';
import type { Book, BookCreate } from '@ring/shared/books';

export type { Book };

export async function listBooks(): Promise<Book[]> {
  return (await apiFetch<{ books: Book[] }>('/api/books', { method: 'GET' })).books;
}

export function createBook(body: BookCreate): Promise<Book> {
  return apiFetch<Book>('/api/books', { method: 'POST', body: JSON.stringify(body) });
}

/** The caller's books, and `add` to create one and put it in the list. books is null while loading. */
export function useBooks(): { books: Book[] | null; error: string | null; add: (body: BookCreate) => Promise<Book> } {
  const [books, setBooks] = useState<Book[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    listBooks()
      .then(b => { if (live) setBooks(b); })
      .catch(err => { if (live) setError(err instanceof Error ? err.message : 'Couldn\'t load your books.'); });
    return () => { live = false; };
  }, []);

  const add = useCallback(async (body: BookCreate) => {
    const book = await createBook(body);
    setBooks(prev => [...(prev ?? []), book].sort((a, b) =>
      a.bookCode.localeCompare(b.bookCode, undefined, { numeric: true, sensitivity: 'base' })));
    return book;
  }, []);

  return { books, error, add };
}
